import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import NlimsDataTable from '../../../shared/NlimsDataTable/NlimsDataTable';
import CreateSpeciesModal from '../../Modals/CreateSpeciesModal';
import EditSpeciesModal from '../../Modals/EditSpeciesModal';
import RecordDetailsModal from '../../shared/RecordDetailsModal';
import { speciesApi } from '../../../../api/speciesLivestockRegistryApi';
import {
  actionMenuHtml,
  boolBadgeHtml,
  humanize,
  humanizedCellHtml,
  parseApiError,
  statusBadgeHtml,
  textCellHtml,
} from './registryHelpers';
import RegistryToolbar from './RegistryToolbar';
import SystemRowMenu, { standardRowItems, confirmAction } from '../../shared/SystemRowMenu';
import { anchorRect } from '../../../shared/useAnchoredMenu';

const MANDATE_TONES = {
  active:   'background:#dcfce7;color:#15803d;border:1px solid #86efac;',
  reserved: 'background:#fffbeb;color:#b45309;border:1px solid #fde68a;',
  excluded: 'background:#fee2e2;color:#b91c1c;border:1px solid #fecaca;',
  default:  'background:#f3f4f6;color:#6b7280;border:1px solid #e5e7eb;',
};

const COLUMNS = [
  { title: 'Species Code',                data: 'species_code',               sortable: true, render: textCellHtml },
  { title: 'Common Name Nigeria',         data: 'common_name_nigeria',         sortable: true, render: textCellHtml },
  { title: 'Scientific Name',             data: 'scientific_name',             sortable: true, render: textCellHtml },
  { title: 'Species Category',            data: 'species_category',            sortable: true, render: humanizedCellHtml },
  { title: 'Sex Disaggregation Required', data: 'sex_disaggregation_required', sortable: true, render: boolBadgeHtml },
  { title: 'Mandate Status',              data: 'mandate_status',              sortable: true, render: (value, type) => statusBadgeHtml(value, type, MANDATE_TONES) },
  { title: 'Mandate Decision Ref',        data: 'mandate_decision_ref',        sortable: true, render: textCellHtml },
  { title: 'Is Validated',               data: 'is_validated',                sortable: true, render: boolBadgeHtml },
  { title: 'Action', data: null, sortable: false, render: (_d, type) => (type === 'display' ? actionMenuHtml() : '') },
];

export default function SpeciesRegistry() {
  const [searchDraft, setSearchDraft] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [viewRow, setViewRow] = useState(null);
  const [rowMenu, setRowMenu] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    speciesApi.listAll({ per_page: 200 })
      .then(({ items }) => { if (active) setRecords(items); })
      .catch((err) => { if (active) setError(parseApiError(err, 'Failed to load species.')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [refreshTick]);

  const handleSearch = () => setSearchQuery(searchDraft.trim());
  const handleRefresh = () => setRefreshTick((v) => v + 1);

  const handleCreate = async (payload) => {
    await speciesApi.create(payload);
    toast.success('Species created.', { position: 'top-right' });
    handleRefresh();
  };

  const handleSave = async (payload) => {
    if (!payload?.uuid) throw new Error('Missing species identifier.');
    await speciesApi.update(payload.uuid, payload);
    toast.success('Species updated.', { position: 'top-right' });
    handleRefresh();
  };

  const handleDelete = async (row) => {
    if (!row?.uuid) return;
    const label = row.common_name_nigeria || row.species_code || 'this species';
    if (!(await confirmAction({ title: 'Delete Record?', message: `Delete ${label}?`, confirmLabel: 'Delete', danger: true }))) return;
    setDeleting(true);
    try {
      await speciesApi.remove(row.uuid);
      setViewRow(null);
      toast.success('Species removed.', { position: 'top-right' });
      handleRefresh();
    } catch (err) {
      toast.error(parseApiError(err, 'Unable to delete species.'), { position: 'top-right' });
    } finally {
      setDeleting(false);
    }
  };

  const handleRowAction = (action, row, meta) => {
    if (!row) return;
    if (action === 'menu') { setRowMenu({ row, rect: anchorRect(meta?.button) }); return; }
    if (action === 'edit') setEditRow(row);
  };

  return (
    <section>
      <RegistryToolbar
        search={searchDraft}
        setSearch={setSearchDraft}
        onSearch={handleSearch}
        onCreate={() => setCreateOpen(true)}
        createLabel="Create New"
      />

      {error && (
        <div className="mb-4 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>{error}</span>
            <button type="button" onClick={handleRefresh}
              className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100">
              Retry
            </button>
          </div>
        </div>
      )}

      {loading && (
        <p className="mb-3 text-sm text-gray-500">Loading species...</p>
      )}

      <NlimsDataTable
        tableId="species-table"
        columns={COLUMNS}
        data={records}
        serverSide={false}
        searchQuery={searchQuery}
        hideSearch
        pageLength={10}
        defaultSort={[0, 'asc']}
        onRowAction={handleRowAction}
      />

      {rowMenu && (
        <SystemRowMenu
          rect={rowMenu.rect}
          items={standardRowItems()}
          onClose={() => setRowMenu(null)}
          onPick={(key) => { const row = rowMenu.row; if (key === 'view') setViewRow(row); if (key === 'edit') setEditRow(row); if (key === 'delete') handleDelete(row); }}
        />
      )}

      <RecordDetailsModal
        isOpen={!!viewRow}
        onClose={() => setViewRow(null)}
        onEdit={() => { setEditRow(viewRow); setViewRow(null); }}
        onDelete={() => handleDelete(viewRow)}
        deleting={deleting}
        title="Species Details"
        subtitle={viewRow?.common_name_nigeria || viewRow?.species_code}
        statusValue={viewRow?.mandate_status}
        fields={viewRow ? [
          { label: 'Species Code',                value: viewRow.species_code || '-' },
          { label: 'Common Name Nigeria',         value: viewRow.common_name_nigeria || '-' },
          { label: 'Scientific Name',             value: viewRow.scientific_name || '-' },
          { label: 'Species Category',            value: humanize(viewRow.species_category) },
          { label: 'Sex Disaggregation Required', value: viewRow.sex_disaggregation_required ? 'Yes' : 'No' },
          { label: 'Mandate Status',              value: humanize(viewRow.mandate_status) },
          { label: 'Mandate Decision Ref',        value: viewRow.mandate_decision_ref || '-' },
          { label: 'Is Validated',               value: viewRow.is_validated ? 'Yes' : 'No' },
          { label: 'UUID', value: viewRow.uuid, mono: true, wide: true },
        ] : []}
      />

      <CreateSpeciesModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
      />

      <EditSpeciesModal
        isOpen={Boolean(editRow)}
        onClose={() => setEditRow(null)}
        onSave={handleSave}
        row={editRow}
      />
    </section>
  );
}
